import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserCircle, EnvelopeSimple, Check } from "@phosphor-icons/react";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await updateProfile(name.trim());
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-3xl" data-testid="profile-page">
      <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Account</div>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Your profile</h1>
      <p className="mt-3 text-sm text-ink-secondary max-w-lg">
        Manage how you appear inside ApexGrey. Email is provided by Google and cannot be changed here.
      </p>

      <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8">
        <div className="flex items-center gap-5">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-16 w-16 rounded-full border border-white/10 object-cover" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-graphite-700 grid place-items-center text-xl font-display" data-testid="profile-avatar-fallback">
              {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-display text-xl tracking-tight" data-testid="profile-display-name">{user?.name || "Unnamed"}</div>
            <div className="text-sm text-ink-secondary" data-testid="profile-display-email">{user?.email}</div>
          </div>
        </div>

        <form onSubmit={handleSave} className="mt-8 space-y-6">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary flex items-center gap-2">
              <UserCircle weight="duotone" className="h-4 w-4" /> Full name
            </label>
            <Input
              data-testid="profile-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="bg-graphite-800 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary flex items-center gap-2">
              <EnvelopeSimple weight="duotone" className="h-4 w-4" /> Email (read-only)
            </label>
            <Input
              data-testid="profile-email-input"
              value={user?.email || ""}
              disabled
              readOnly
              className="bg-graphite-800 border-white/10 text-ink-secondary/80 cursor-not-allowed"
            />
            <p className="text-[11px] text-ink-secondary/70">Your email is managed by Google Sign-In.</p>
          </div>

          <div className="pt-2 flex items-center gap-3">
            <button
              type="submit"
              data-testid="profile-save-button"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60 transition-colors"
            >
              <Check weight="bold" className="h-4 w-4" />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
